// Whether.markets — Layered Index Markets
// City → Regional → Country → Global aggregation
// Plus Energy/GDP macro indices for parametric insurance pricing
//
// Architecture:
//   Individual city perps (HDD, CDD, Temp) generate price discovery
//   → Regional indices aggregate cities by geography
//   → Country indices aggregate regions
//   → Global indices aggregate countries
//   Liquidity flows up and down: hedgers at any layer can find counterparties

import { MarketConfig, StationConfig, Region, ProductType, LaunchPhase } from './markets';

// ─── Index Types ─────────────────────────────────────────────────────────────

export type IndexLevel = 'city' | 'regional' | 'country' | 'global';
export type IndexCategory = 'weather' | 'energy' | 'macro' | 'insurance';

export interface IndexMarket extends MarketConfig {
  indexLevel: IndexLevel;
  indexCategory: IndexCategory;
  components: string[];       // IDs of underlying markets
  weightMethod: 'equal' | 'population' | 'gdp' | 'energy_consumption';
  description: string;
}

// ─── Helper ──────────────────────────────────────────────────────────────────

function idxStation(city: string, country: string, region: Region): StationConfig {
  return {
    id: `IDX-${city.replace(/\s/g, '').toUpperCase()}`,
    name: `${city} Index`,
    city, country, region,
    lat: 0, lon: 0,
    timezone: 'UTC',
    elevation_ft: 0,
  };
}

function idx(
  id: string, name: string, shortName: string, type: ProductType,
  city: string, country: string, region: Region,
  level: IndexLevel, category: IndexCategory,
  components: string[], weightMethod: IndexMarket['weightMethod'],
  formula: string, description: string,
  phase: LaunchPhase = 2, lev: number = 10,
): IndexMarket {
  return {
    id, name, shortName, type,
    station: idxStation(city, country, region),
    indexFormula: formula,
    windowDays: 7,
    resolutionFreq: 'daily',
    maxLeverage: lev,
    mmrBps: 100,
    fundingCapBps: 50,
    tickValue: '0.1',
    referenceTemp: 65,
    unit: 'index',
    seasonal: false,
    launchPhase: phase,
    oracleSource: 'Whether Oracle (aggregated)',
    cmeParity: 'N/A',
    indexLevel: level,
    indexCategory: category,
    components,
    weightMethod,
    description,
  };
}

// ============================================================================
// REGIONAL WEATHER INDICES
// Aggregate city-level HDD/CDD/Temp into geographic regions
// ============================================================================

export const REGIONAL_WEATHER_INDICES: IndexMarket[] = [
  // ── US Regional ──────────────────────────────────────────────────────────
  idx('idx-hdd-us-northeast', 'US Northeast HDD Index', 'NE HDD', 'hdd',
    'Northeast US', 'US', 'us', 'regional', 'weather',
    ['hdd-nyc-7d', 'hdd-bos-7d', 'hdd-phl-7d', 'hdd-bwi-7d', 'hdd-dca-7d'],
    'population',
    'Pop-weighted avg HDD: NYC(40%) + BOS(15%) + PHL(15%) + BWI(15%) + DCA(15%)',
    'Population-weighted heating demand across the US Northeast corridor. Key driver for residential gas consumption.',
  ),
  idx('idx-hdd-us-midwest', 'US Midwest HDD Index', 'MW HDD', 'hdd',
    'Midwest US', 'US', 'us', 'regional', 'weather',
    ['hdd-ord-7d', 'hdd-dtw-7d', 'hdd-msp-7d', 'hdd-dsm-7d', 'hdd-mci-7d'],
    'population',
    'Pop-weighted avg HDD: CHI(35%) + DET(20%) + MSP(15%) + DSM(15%) + KC(15%)',
    'Heartland heating demand. Drives natural gas pipeline flows from Gulf Coast and Permian.',
  ),
  idx('idx-cdd-us-south', 'US South CDD Index', 'South CDD', 'cdd',
    'South US', 'US', 'us', 'regional', 'weather',
    ['cdd-iah-7d', 'cdd-dfw-7d', 'cdd-atl-7d', 'cdd-mia-7d', 'cdd-jax-7d'],
    'population',
    'Pop-weighted avg CDD: HOU(25%) + DFW(25%) + ATL(20%) + MIA(15%) + JAX(15%)',
    'Cooling demand across the US Sunbelt. Key driver for ERCOT/SERC power demand.',
  ),
  idx('idx-cdd-us-west', 'US West CDD Index', 'West CDD', 'cdd',
    'West US', 'US', 'us', 'regional', 'weather',
    ['cdd-lax-7d', 'cdd-las-7d', 'cdd-smf-7d', 'cdd-sea-7d', 'cdd-pdx-7d'],
    'population',
    'Pop-weighted avg CDD: LA(35%) + LV(20%) + SAC(15%) + SEA(15%) + PDX(15%)',
    'West Coast cooling demand. CAISO power pricing driver.',
  ),

  // ── European Regional ────────────────────────────────────────────────────
  idx('idx-hdd-eu-northwest', 'NW Europe HDD Index', 'NW EU HDD', 'hdd',
    'NW Europe', 'EU', 'europe', 'regional', 'weather',
    ['hdd-ams-7d', 'hdd-lon-7d', 'hdd-par-7d', 'hdd-ess-7d'],
    'energy_consumption',
    'Energy-weighted avg HDD: AMS(25%) + LON(25%) + PAR(25%) + ESS(25%)',
    'Core European heating demand. TTF gas pricing driver. EU storage mandate pressure gauge.',
  ),
  idx('idx-hdd-eu-central', 'Central Europe HDD Index', 'CE HDD', 'hdd',
    'Central Europe', 'EU', 'europe', 'regional', 'weather',
    ['hdd-ber-7d', 'hdd-pra-7d'],
    'energy_consumption',
    'Energy-weighted avg HDD: Berlin(60%) + Prague(40%)',
    'German/Czech heating demand. EEX power and TTF gas driver.',
  ),
  idx('idx-hdd-eu-nordic', 'Nordic HDD Index', 'Nordic HDD', 'hdd',
    'Nordics', 'NO', 'europe', 'regional', 'weather',
    ['hdd-osl-7d'],
    'population',
    'Nordic heating demand (Oslo)',
    'Nordic heating demand. Nord Pool power and heating oil driver.',
  ),

  // ── APAC Regional ────────────────────────────────────────────────────────
  idx('idx-cdd-apac-sea', 'Southeast Asia CDD Index', 'SEA CDD', 'cdd',
    'Southeast Asia', 'SG', 'apac', 'regional', 'weather',
    ['cdd-sin-7d', 'cdd-bkk-7d'],
    'population',
    'Pop-weighted avg CDD: Singapore(50%) + Bangkok(50%)',
    'Tropical cooling demand. LNG import demand for power generation.',
  ),
  idx('idx-cdd-apac-east', 'East Asia CDD Index', 'EA CDD', 'cdd',
    'East Asia', 'JP', 'apac', 'regional', 'weather',
    ['cdd-tyo-7d', 'cdd-icn-7d'],
    'population',
    'Pop-weighted avg CDD: Tokyo(55%) + Seoul(45%)',
    'Northeast Asia cooling demand. JKM LNG pricing driver.',
  ),
  idx('idx-cdd-me', 'Middle East CDD Index', 'ME CDD', 'cdd',
    'Middle East', 'AE', 'middle_east', 'regional', 'weather',
    ['cdd-dxb-7d'],
    'population',
    'Dubai cooling demand',
    'Gulf cooling demand. Desalination energy costs. Summer peak power.',
  ),
];

// ============================================================================
// COUNTRY WEATHER INDICES
// Aggregate regional indices into country-level
// ============================================================================

export const COUNTRY_WEATHER_INDICES: IndexMarket[] = [
  idx('idx-hdd-us', 'US National HDD Index', 'US HDD', 'hdd',
    'United States', 'US', 'us', 'country', 'weather',
    ['idx-hdd-us-northeast', 'idx-hdd-us-midwest'],
    'population',
    'Pop-weighted: NE(55%) + MW(45%)',
    'US national heating demand. Drives EIA weekly gas storage draws. Benchmark for US gas price direction.',
  ),
  idx('idx-cdd-us', 'US National CDD Index', 'US CDD', 'cdd',
    'United States', 'US', 'us', 'country', 'weather',
    ['idx-cdd-us-south', 'idx-cdd-us-west'],
    'population',
    'Pop-weighted: South(60%) + West(40%)',
    'US national cooling demand. Summer power burn. Air conditioning load on gas-fired generation.',
  ),
  idx('idx-hdd-eu', 'EU HDD Index', 'EU HDD', 'hdd',
    'European Union', 'EU', 'europe', 'country', 'weather',
    ['idx-hdd-eu-northwest', 'idx-hdd-eu-central', 'idx-hdd-eu-nordic'],
    'energy_consumption',
    'Energy-weighted: NW(50%) + CE(35%) + Nordic(15%)',
    'EU-wide heating demand. TTF gas benchmark. EU storage mandate compliance pressure.',
  ),
  idx('idx-cdd-apac', 'Asia-Pacific CDD Index', 'APAC CDD', 'cdd',
    'Asia-Pacific', 'SG', 'apac', 'country', 'weather',
    ['idx-cdd-apac-sea', 'idx-cdd-apac-east', 'idx-cdd-me'],
    'gdp',
    'GDP-weighted: SEA(25%) + EA(50%) + ME(25%)',
    'Asia-Pacific cooling demand. JKM LNG pricing driver. Cargo routing signal between Atlantic and Pacific basins.',
  ),
];

// ============================================================================
// GLOBAL WEATHER INDICES
// Aggregate country indices into global
// ============================================================================

export const GLOBAL_WEATHER_INDICES: IndexMarket[] = [
  idx('idx-hdd-global', 'Global HDD Index', 'World HDD', 'hdd',
    'Global', 'GLOBAL', 'global', 'global', 'weather',
    ['idx-hdd-us', 'idx-hdd-eu'],
    'energy_consumption',
    'Energy-weighted: US(45%) + EU(55%)',
    'Global heating demand. Macro indicator for winter energy prices across all hubs.',
  ),
  idx('idx-cdd-global', 'Global CDD Index', 'World CDD', 'cdd',
    'Global', 'GLOBAL', 'global', 'global', 'weather',
    ['idx-cdd-us', 'idx-cdd-apac'],
    'population',
    'Pop-weighted: US(30%) + APAC(70%)',
    'Global cooling demand. Summer energy stress indicator.',
  ),
  idx('idx-weather-global', 'Global Weather Stress Index', 'GWSI', 'temp',
    'Global', 'GLOBAL', 'global', 'global', 'weather',
    ['idx-hdd-global', 'idx-cdd-global'],
    'equal',
    'abs(HDD deviation) + abs(CDD deviation) from 30yr normals',
    'Combined heating + cooling stress. Higher = more extreme weather = higher energy costs. Insurance pricing signal.',
  ),
];

// ============================================================================
// ENERGY / GDP MACRO INDICES
// Energy consumed per capita, GDP per capita, efficiency scores
// These enable parametric insurance pricing by country
// ============================================================================

export const MACRO_ENERGY_INDICES: IndexMarket[] = [
  // ── Energy per Capita ────────────────────────────────────────────────────
  idx('idx-energy-capita-us', 'US Energy/Capita', 'US E/Cap', 'power',
    'United States', 'US', 'us', 'country', 'macro',
    ['natgas-hh', 'crude-wti', 'power-pjm', 'power-ercot'],
    'equal',
    'EIA total energy / US Census population (BTU/capita, indexed)',
    'US energy intensity per person. Proxy for standard of living and industrialization.',
    3, 5,
  ),
  idx('idx-energy-capita-eu', 'EU Energy/Capita', 'EU E/Cap', 'power',
    'European Union', 'EU', 'europe', 'country', 'macro',
    ['natgas-ttf', 'crude-brent', 'power-de'],
    'equal',
    'Eurostat final energy / EU population (kWh/capita, indexed)',
    'EU energy intensity per person. Efficiency leader vs US.',
    3, 5,
  ),
  idx('idx-energy-capita-apac', 'APAC Energy/Capita', 'APAC E/Cap', 'power',
    'Asia-Pacific', 'SG', 'apac', 'country', 'macro',
    ['natgas-jkm', 'crude-dubai'],
    'gdp',
    'IEA APAC energy / population (weighted by country GDP)',
    'Asia-Pacific energy intensity. Rapidly growing as industrialization accelerates.',
    3, 5,
  ),

  // ── GDP per Capita ───────────────────────────────────────────────────────
  idx('idx-gdp-capita-us', 'US GDP/Capita Index', 'US GDP/Cap', 'temp',
    'United States', 'US', 'us', 'country', 'macro',
    [],
    'equal',
    'BEA GDP / Census pop (quarterly, indexed to 100)',
    'US economic output per person. Benchmark for global productivity.',
    3, 5,
  ),
  idx('idx-gdp-capita-eu', 'EU GDP/Capita Index', 'EU GDP/Cap', 'temp',
    'European Union', 'EU', 'europe', 'country', 'macro',
    [],
    'equal',
    'Eurostat GDP / population (quarterly, indexed to 100)',
    'EU economic output per person. Divergence from US = structural competitiveness signal.',
    3, 5,
  ),

  // ── Energy Efficiency Score (GDP per unit of energy) ─────────────────────
  idx('idx-efficiency-us', 'US Energy Efficiency Score', 'US Eff', 'spread',
    'United States', 'US', 'us', 'country', 'macro',
    ['idx-gdp-capita-us', 'idx-energy-capita-us'],
    'equal',
    'GDP/capita / Energy/capita = $ output per BTU consumed',
    'How much GDP the US generates per unit of energy. Rising = decarbonization success. Falling = energy waste.',
    3, 5,
  ),
  idx('idx-efficiency-eu', 'EU Energy Efficiency Score', 'EU Eff', 'spread',
    'European Union', 'EU', 'europe', 'country', 'macro',
    ['idx-gdp-capita-eu', 'idx-energy-capita-eu'],
    'equal',
    'GDP/capita / Energy/capita = EUR output per kWh consumed',
    'EU economic efficiency. EU typically 30-40% more efficient than US due to higher energy costs driving conservation.',
    3, 5,
  ),
  idx('idx-efficiency-global', 'Global Energy Efficiency Index', 'World Eff', 'spread',
    'Global', 'GLOBAL', 'global', 'global', 'macro',
    ['idx-efficiency-us', 'idx-efficiency-eu'],
    'gdp',
    'GDP-weighted global efficiency score',
    'Global economic efficiency of energy use. Insurance pricing benchmark: countries below this line pay higher premiums.',
    3, 5,
  ),

  // ── Energy Cost Burden (local energy cost / local GDP) ───────────────────
  idx('idx-energy-burden-us', 'US Energy Cost Burden', 'US Burden', 'spread',
    'United States', 'US', 'us', 'country', 'macro',
    ['natgas-hh', 'crude-wti', 'idx-gdp-capita-us'],
    'equal',
    'Household energy spend / median income (%)',
    'Energy affordability index. Higher = more vulnerable to price spikes. Insurance trigger for low-income assistance.',
    3, 5,
  ),
  idx('idx-energy-burden-eu', 'EU Energy Cost Burden', 'EU Burden', 'spread',
    'European Union', 'EU', 'europe', 'country', 'macro',
    ['natgas-ttf', 'crude-brent', 'idx-gdp-capita-eu'],
    'equal',
    'Household energy spend / median income (%)',
    'EU energy poverty indicator. High TTF = energy poverty crisis. Insurance pricing signal for EU parametric products.',
    3, 5,
  ),
];

// ============================================================================
// INSURANCE PRICING INDICES
// Cross weather stress with energy burden for parametric insurance
// ============================================================================

export const INSURANCE_INDICES: IndexMarket[] = [
  idx('idx-insurance-us', 'US Parametric Insurance Index', 'US Ins', 'temp',
    'United States', 'US', 'us', 'country', 'insurance',
    ['idx-weather-global', 'idx-energy-burden-us', 'idx-efficiency-us'],
    'equal',
    'Weather stress x Energy burden / Efficiency score',
    'Combined risk score for US parametric insurance pricing. Higher = more premium needed. Lower = cheaper coverage.',
    4, 5,
  ),
  idx('idx-insurance-eu', 'EU Parametric Insurance Index', 'EU Ins', 'temp',
    'European Union', 'EU', 'europe', 'country', 'insurance',
    ['idx-weather-global', 'idx-energy-burden-eu', 'idx-efficiency-eu'],
    'equal',
    'Weather stress x Energy burden / Efficiency score',
    'EU parametric insurance pricing signal. Tonsurance uses this to quote weather-linked energy insurance policies.',
    4, 5,
  ),
  idx('idx-insurance-global', 'Global Insurance Risk Index', 'GIRI', 'temp',
    'Global', 'GLOBAL', 'global', 'global', 'insurance',
    ['idx-insurance-us', 'idx-insurance-eu'],
    'gdp',
    'GDP-weighted global insurance risk',
    'Master index for parametric insurance pricing across all Whether/Tonsurance products. The pricing oracle for the entire insurance layer.',
    4, 5,
  ),
];

// ============================================================================
// COMBINED EXPORT
// ============================================================================

export const ALL_INDEX_MARKETS: IndexMarket[] = [
  ...REGIONAL_WEATHER_INDICES,
  ...COUNTRY_WEATHER_INDICES,
  ...GLOBAL_WEATHER_INDICES,
  ...MACRO_ENERGY_INDICES,
  ...INSURANCE_INDICES,
];

// Tab categories for the MarketsPage
export const INDEX_TABS = [
  { id: 'regional', label: 'Regional', description: 'City-aggregated regional indices' },
  { id: 'country', label: 'Country', description: 'Country-level national indices' },
  { id: 'global', label: 'Global', description: 'Global benchmark indices' },
  { id: 'macro', label: 'Macro', description: 'Energy/GDP per capita indices' },
  { id: 'insurance', label: 'Insurance', description: 'Parametric insurance pricing indices' },
] as const;
